/**
 * Dashboard Page - Historic analysis list & upload
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { newsletterAPI } from '../services/api';

export const DashboardPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [newsletters, setNewsletters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadNewsletters();
  }, []);

  const loadNewsletters = async () => {
    try {
      setLoading(true);
      const { data } = await newsletterAPI.getAll();
      setNewsletters(data);
    } catch (err) {
      setError('Failed to load newsletters');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      setError('');
      const formData = new FormData();
      formData.append('file', file);

      const { data } = await newsletterAPI.upload(formData);
      setNewsletters([data, ...newsletters]);
      e.target.value = ''; // Reset input

      // Redirect to analyzer
      navigate(`/analyzer/${data._id}`);
    } catch (err) {
      setError(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this analysis?')) return;

    try {
      await newsletterAPI.delete(id);
      setNewsletters(newsletters.filter((n) => n._id !== id));
    } catch (err) {
      setError('Failed to delete newsletter');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">Newsletter Scanner</h1>
          <div className="flex items-center gap-4">
            <span className="text-gray-600">{user?.email}</span>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Upload Section */}
        <div className="bg-blue-50 border-2 border-dashed border-blue-300 rounded-lg p-8 mb-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Upload Newsletter</h2>
            <p className="text-gray-600 mb-6">Drop a .zip file containing your newsletter HTML</p>

            <label className="inline-block">
              <input
                type="file"
                accept=".zip"
                onChange={handleFileUpload}
                disabled={uploading}
                className="hidden"
              />
              <span className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer disabled:bg-gray-400">
                {uploading ? 'Uploading...' : 'Choose File'}
              </span>
            </label>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Newsletters List */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Analysis History</h2>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading analyses...</p>
            </div>
          ) : newsletters.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <p className="text-gray-500">No analyses yet. Upload your first newsletter!</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {newsletters.map((newsletter) => (
                <div
                  key={newsletter._id}
                  className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition cursor-pointer border border-gray-200"
                >
                  <div className="flex justify-between items-start">
                    <div
                      onClick={() => navigate(`/analyzer/${newsletter._id}`)}
                      className="flex-1"
                    >
                      <h3 className="text-lg font-semibold text-gray-900">
                        {newsletter.fileName}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {newsletter.links?.length || 0} links detected •{' '}
                        {new Date(newsletter.uploadedAt).toLocaleDateString()}
                      </p>
                      <div className="mt-3 flex gap-4">
                        <span className="text-sm">
                          <span className="font-semibold text-green-600">
                            {newsletter.links?.filter((l) => l.classification === 'normal').length || 0}
                          </span>{' '}
                          normal
                        </span>
                        <span className="text-sm">
                          <span className="font-semibold text-yellow-600">
                            {newsletter.links?.filter((l) => l.classification === 'warning').length || 0}
                          </span>{' '}
                          warning
                        </span>
                        <span className="text-sm">
                          <span className="font-semibold text-red-600">
                            {newsletter.links?.filter((l) => l.classification === 'fake').length || 0}
                          </span>{' '}
                          fake
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDelete(newsletter._id)}
                      className="px-3 py-2 text-red-600 hover:bg-red-50 rounded"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};
